import { DIS } from './dis';

export namespace DOMINO {
  export interface Country {
    id: string;
    countryName: string;
    shortCode: string;
  }

  export interface Countries {
    countries: Country[];
  }

  export interface Language {
    id: string;
    shortCode: string;
    languageName: string;
    ispreferred: boolean;
  }

  export interface Languages {
    languages: Language[];
  }

  export interface AreaOfExpertise {
    id: string;
    value: string;
    detail: object;
  }
  export enum SharingPermissions {
    noSharing = 'No Sharing',
    shareWithClubAndDistrict = 'Share with my club',
    shareWithDistrictOfficer = 'Share with my district',
    shareWithAll = 'Share with all members',
  }

  export enum ClubStatus {
    Active = 'Active',
    Terminated = 'Terminated',
    Suspended = 'Suspended',
  }

  export interface PhoneOutput {
    country: string;
    countryId: string;
    extension: string | null;
    number: string;
    isPrimary: boolean;
    isFax: boolean;
    id?: string | null;
    action?: string;
  }

  export interface EmailOutput {
    email: string;
    isPrimary: boolean;
    id?: string;
    action?: string;
  }

  export interface WebsiteOutput {
    primaryWebsite: string;
    otherWebsites?: string;
    id?: string;
    action?: string;
  }

  export interface AddressBaseOutput {
    state: string | null;
    country: string | null;
    lineOne: string | null;
    city: string | null;
    postalCode: string | null;
    countryId: string;
    internationalProvince: string | null;
    lineThree: string | null;
    lineTwo: string | null;
    stateId: string | null;
    id?: string;
    action?: string;
    type: string;
  }

  export type AddressType =
    | DominoAddressType
    | 'BusinessAddresses'
    | 'Home'
    | 'LocalizedAddress'
    | 'SubscriptionMailingAddresses'
    | 'Project'
    | 'HostCountry'
    | 'Unknown'
    | 'MailingAddress'
    | 'PhysicalLocation';

  export type DominoAddressType =
    | 'Business'
    | 'Localized'
    | 'Subscription Mailing'
    | 'Rotary Business'
    | 'Physical Location';

  export interface AddressOutput extends AddressBaseOutput {
    type: AddressType;
  }

  export interface LanguageOutput {
    language: string;
    languageId: string;
  }

  export type PhoneType = 'Business' | 'Home';

  export type Weekday =
    | 'Monday'
    | 'Tuesday'
    | 'Wednesday'
    | 'Thursday'
    | 'Friday'
    | 'Saturday'
    | 'Sunday';

  export interface District {
    id: string;
    riDistrictId: number;
    districtDisId: string;
  }

  export type ClubType =
    | 'Rotary_Club'
    | 'Rotaract_Club'
    | 'Satellite_Club'
    | 'Rotaract_Satellite_Club';

  export type OrganizationBase = 'School' | 'Community' | 'University';

  export interface ClubRelationship {
    id: string;
    type: ClubType;
    startDate: string;
    endDate: string;
    childClubId?: string;
    childDisId?: string;
    parentClubId?: string;
    parentDisId?: string;
    clubName: string;
  }

  export interface Meeting {
    address: AddressBaseOutput | null;
    latitude: number | null;
    longitude: number | null;
    weekday: Weekday;
    time: string;
    comment: string | null;
    type: MeetingType;
    onlineLocation: string | null;
    languageId: string | null;
    language: string | null;
    frequency: string | null;
    name: string | null;
    locationNameOrDetail: string | null;
    locationVaries: boolean;
    detailForJoiningRemotely: string | null;
    meetingId: string | null;
  }

  export type EditOrgContactInfo = {
    contactInfo: ContactInfoType;
    id: string;
  };

  export interface EditOrgContactInfoPayload {
    contactInfo: ContactInfoType;
  }

  export type ContactInfoType = Partial<{
    addresses: AddressBaseInput[] | AddressBaseInput;
    emails: EmailInput[] | EmailInput;
    phones: PhoneInput[];
    websites: WebsitesInput[] | WebsitesInput;
    officialLanguage?: Omit<LanguageOutput, 'language'>;
  }>;

  export type AddressBaseInput = AddressBaseOutput & { action?: string };

  export type EmailInput = EmailOutput & { action?: string };

  export type WebsitesInput = Omit<WebsiteOutput, 'otherWebsites'> & {
    action?: string;
  };

  export type PhoneInput = Omit<PhoneOutput & { action?: string }, 'country'>;

  export interface Club {
    status: ClubStatus;
    phones: PhoneOutput[];
    addresses: AddressOutput[];
    riClubId: string;
    id: string;
    clubDisId: string;
    name: string;
    charteredDate: string;
    officialLanguage: LanguageOutput;
    emails: EmailOutput[];
    websites: WebsiteOutput;
    district: District | null;
    clubSponsorOf: ClubRelationship[];
    clubSponsoredBy: ClubRelationship[];
    organizationBase: OrganizationBase | null;
    meetings: Meeting[];
    type: ClubType;
  }

  export type MeetingType = 'Physical' | 'Online' | 'Hybrid';
  export interface Conference {
    id: string;
    startDate: string;
    startTime: string;
    endDate: string;
    endTime: string;
    relatedRotaryYear: number;
    venueName: string;
    language: string;
    prAccommodation: string;
    comments: string;
    venueType: MeetingType;
    onlineLocation: string;
    locationNameOrDetail: string;
    detailForJoiningRemotely: string;
    districtId: string;
    conferenceAddress: AddressBaseInput;
    prAddress: AddressBaseInput;
    timeZone: string;
    isPresRepRequested: boolean;
    presRepAccPhone: Omit<PhoneInput, 'isPrimary' | 'isFax'>;
    presidentialRepresentative: PartialIndividualData;
    participatingDistricts: District[];
    feedbackList: Feedback[];
  }

  export enum FeedbackType {
    DG = 'DG',
    PR = 'PR',
  }
  export interface Feedback {
    id: string;
    feedbackType: FeedbackType;
  }

  export interface PartialIndividualData {
    id: string;
    firstName: string;
    lastName: string;
    middleName?: string | null;
    prefix?: string | null;
    suffix?: string | null;
    localizedName?: string | null;
  }

  export interface GetAllLeadershipsOutput {
    page: number;
    pageSize: number;
    totalCount: number;
    leadership: Leadership[];
  }
  export interface Leadership {
    id: string;
    endDate: string;
    startDate: string;
    isDelegated: boolean;
    roleId: string;
    role: string;
    individual: PartialIndividualData;
  }

  export interface State {
    code: string;
    stateName: string;
    countryCode: string;
  }

  export interface LeadershipRole {
    StartDate: string;
    EndDate: string;
  }

  export interface TerminationReasons {
    terminationReasonPk: string;
    reason: DIS.Reason;
    organizationType: string;
    donotAllowUserentry: null | boolean;
    status: string;
    nfKey: string;
  }

  export interface AreaOfFocus {
    id: string;
    value: string;
    Acronym: string;
    IsActive: null | boolean;
  }

  export interface TimeZone {
    id: string;
    value: string;
    timeZoneName: string;
    location: string;
    offset: string;
  }
  export interface IndividualLanguages {
    id: string;
    languageId: string;
    languageName: string;
    isPrimary: boolean;
    ispreferred: boolean;
  }

  export interface IndividualEmails {
    id: string;
    type: string;
    address: string;
    isPrimary: boolean;
  }

  export interface IndividualAddress {
    id: string;
    type: string;
    lineOne: string;
    lineTwo: string;
    lineThree: string;
    countryId: string;
    countryName: string;
    city: string;
    stateId: string;
    stateName: string;
    internationalProvince: string;
    postalCode: string;
    isPrimary: boolean;
  }

  export interface IndividualPhone extends Omit<PhoneOutput, 'country'> {
    id: string;
    type: string;
    number: string;
    countryId: string;
    countryName: string;
    extension: string;
    isPrimary: boolean;
    isFax: boolean;
  }

  export interface IndividualProfession {
    id: string;
    position: string;
    employer: string;
    occupation: string;
    isPrimary?: boolean;
    action?: string;
  }

  export interface IndividualExpertise {
    id: string;
    areaId: string;
    area: string;
    levelId: string;
    level: string;
    isPrimary?: boolean;
    action?: string;
  }

  export interface SharingPermissionBase {
    level: string;
    levelId: string;
  }

  export interface IndividualSharingPermissions {
    name: SharingPermissionBase | null;
    gender: SharingPermissionBase | null;
    email: SharingPermissionBase | null;
    phone: SharingPermissionBase | null;
    language: SharingPermissionBase | null;
    expertise: SharingPermissionBase | null;
    profession: SharingPermissionBase | null;
    address: SharingPermissionBase | null;
    fax: SharingPermissionBase | null;
    aboutMe: SharingPermissionBase | null;
    program: SharingPermissionBase | null;
    photo: SharingPermissionBase | null;
    dateOfBirth: SharingPermissionBase | null;
    sponsors: SharingPermissionBase | null;
    recognition: SharingPermissionBase | null;
    riIndividualId: SharingPermissionBase | null;
  }

  export type Gender = 'Male' | 'Female' | 'Not Given' | 'DNI' | 'Self';

  export type IndividualOutput = {
    id: string;
    firstName: string;
    lastName: string;
    middleName?: string | null;
    prefix?: string | null;
    suffix?: string | null;
    localizedName?: string | null;
    gender: string | null;
    photoUri: string | null;
    riIndividualId: number | null;
    classification: string | null;
    aboutMe: string | null;
    dateOfBirth: string | null;
    youthFlag: boolean;
    yearOfBirth: string | null;
    badgeName: string | null;
    recognitionOptOutFlag: boolean;
    languages: IndividualLanguages[];
    emails: IndividualEmails[];
    addresses: IndividualAddress[];
    phones: IndividualPhone[];
    professions: IndividualProfession[];
    expertises: IndividualExpertise[];
    sharingPermission: IndividualSharingPermissions;
  };

  export interface Fields {
    id: string;
    value: string;
    Description: string;
  }

  export interface ProgramField {
    id: string;
    value: string;
    Fields: Fields[];
  }

  export interface Programs {
    programs: ProgramField[];
  }

  export interface IndividualProgramsOutput {
    programs: DIS.IndividualProgram[];
  }

  export type IndividualBackgroundInput = {
    id: string;
    aboutMe: string;
    sharingPermissionId: string;
  };

  export type AccessLevel =
    | 'No Permission'
    | 'Limited Viewer'
    | 'Viewer'
    | 'Contributor'
    | 'Manager';

  export interface GetAccessLevelsInputRow {
    target: string;
    arguments?: { [key: string]: string };
  }

  export interface GetAccessLevelsInput {
    data: GetAccessLevelsInputRow[];
  }

  export interface GetAccessLevelsOutput {
    data: (GetAccessLevelsInputRow & { level: AccessLevel })[];
  }

  export interface OrganizationData {
    dominoId: string;
  }

  export interface DistrictInfo {
    id: string;
    riDistrictId: number;
    zone: number | null;
    status: string;
    phones: PhoneOutput[];
    addresses: AddressOutput[];
    emails: EmailOutput[];
    websites: WebsiteOutput;
    districtLegalName: string | null;
  }

  export interface PeopleDetail {
    membershipType: string;
    count: number;
  }
  export interface DistrictPeopleGraphDetails {
    peopleGraphDetails: PeopleDetail[];
    totalCount: number;
  }

  export interface ClubsGraphDetails {
    clubType: string;
    count: number;
  }

  export interface DistrictClubsGraphDetails {
    clubsGraphDetails: ClubsGraphDetails[];
    totalCount: number;
  }

  export interface DistrictOfficerDetail {
    id: string;
    firstName: string;
    lastName: string;
    middleName?: string | null;
    localizedName?: string | null;
    prefix?: string | null;
    suffix?: string | null;
    photoUri?: string | null;
    riIndividualId?: number | null;
    primaryEmail: {
      address: string | null;
    };
    primaryPhone: {
      country: string;
      countryId: string;
      extension: string | null;
      number: string;
      areaCode: string | null;
    };
    districtLeadership: DIS.DistrictLeadershipFromSearch[];
    membership: DIS.MembershipFromSearch[];
  }

  export interface ContactInfo {
    email: IndividualEmails[];
    phone: IndividualPhone[];
    address: IndividualAddress[];
  }

  export type ContactInfoDetails = {
    contactInfo: ContactInfoType;
    id: string;
  };

  export interface DistrictConferencesOutput {
    page: number;
    pageSize: number;
    totalCount: number;
    conferences: DistrictConference[];
  }

  export interface DistrictConference {
    id: string;
    districtId: string;
    startDate: string;
    startTime: string;
    endDate: string;
    endTime: string;
    timeZone: string;
    relatedRotaryYear: number;
    venueName: string;
    city: string;
    stateProvince: string;
    country: string;
    language: string;
    prAccommodation: string;
    prAccAddr1: string;
    prAccAddr2: string;
    prAccAddr3: string;
    prAccPhone: string;
    comments: string;
    participatingDistricts: ParticipatingDistrict[];
    venueType: MeetingType;
    onlineLocation: string;
    locationNameOrDetail: string;
    detailForJoiningRemotely: string;
    conferenceAddress: AddressBaseOutput;
    prAddress: AddressBaseOutput;
    presidentialRepresentative: PresidentialRepresentative;
    isPresRepRequested: boolean;
    presRepAccPhone: PhoneInput;
    feedbackList: ConferenceFeedbackList[];
  }

  export interface ParticipatingDistrict {
    id: string;
    riDistrictId: number;
  }

  export interface PresidentialRepresentative {
    id: string;
    firstName: string;
    middleName: string;
    lastName: string;
  }

  export type ConferenceFeedbackList = {
    id: string;
    feedbackType: FeedbackType;
  };

  export interface DistrictConferencesRequestParams {
    associatedDistrictId: string;
    presidentialRepresentativeId?: string;
    MaxStartDate?: string;
    MinStartDate?: string;
  }

  export interface DistrictByQueryParams {
    status: string;
    riDistrictId?: number;
    page: number;
    pageSize: number;
  }

  export interface DistrictsByQuery {
    page: number;
    pageSize: number;
    totalCount: number;
    districts: DistrictInfo[];
  }

  export enum OrganizationTypeEnum {
    District = 'District',
    RotaryClub = 'Rotary_Club',
    RotaractClub = 'Rotaract_Club',
  }
}

export type Maybe<T> = T | null;
export type Scalars = {
  ID: string;
  String: string;
  Boolean: boolean;
  Int: number;
  Float: number;
  Void: any;
};

export enum ClubTypeFilterValue {
  RotaractClub = 'Rotaract_Club',
  RotaractSatelliteClub = 'Rotaract_Satellite_Club',
  RotaryClub = 'Rotary_Club',
  SatelliteClub = 'Satellite_Club',
}

export enum MeetingTypeFilterValue {
  Hybrid = 'Hybrid',
  Online = 'Online',
  Physical = 'Physical',
}

export type SearchClubMeetingFragment = {
  __typename?: 'ClubSearchResultMeeting';
  description?: string | null | undefined;
  xRiLanguageId?: string | null | undefined;
  xRiLanguageName?: string | null | undefined;
  xRiTime?: string | null | undefined;
  xRiWeekday?: string | null | undefined;
  categories?: Array<string> | null | undefined;
  comment?: Array<string> | null | undefined;
  url?: string | null | undefined;
  geo?:
    | {
        __typename?: 'GeoLocation';
        latitude?: number | null | undefined;
        longitude?: number | null | undefined;
      }
    | null
    | undefined;
  xRiAddress?:
    | {
        __typename?: 'BaseAddress';
        city?: string | null | undefined;
        state?: string | null | undefined;
        country?: string | null | undefined;
      }
    | null
    | undefined;
};

export type SearchClubRowFragment = {
  __typename?: 'ClubSearchResult';
  id: string;
  type: ClubType;
  name: string;
  fullName: string;
  meetings: Array<{
    __typename?: 'ClubSearchResultMeeting';
    description?: string | null | undefined;
    xRiLanguageId?: string | null | undefined;
    xRiLanguageName?: string | null | undefined;
    xRiTime?: string | null | undefined;
    xRiWeekday?: string | null | undefined;
    categories?: Array<string> | null | undefined;
    comment?: Array<string> | null | undefined;
    url?: string | null | undefined;
    geo?:
      | {
          __typename?: 'GeoLocation';
          latitude?: number | null | undefined;
          longitude?: number | null | undefined;
        }
      | null
      | undefined;
    xRiAddress?:
      | {
          __typename?: 'BaseAddress';
          city?: string | null | undefined;
          state?: string | null | undefined;
          country?: string | null | undefined;
        }
      | null
      | undefined;
  }>;
};

export enum ClubType {
  RotaractClub = 'RotaractClub',
  RotaractSatelliteClub = 'RotaractSatelliteClub',
  RotaryClub = 'RotaryClub',
  SatelliteClub = 'SatelliteClub',
}

export type QuerySearchClubArgs = {
  advanced?: Maybe<Scalars['Boolean']>;
  clubFullNamePartial?: Maybe<Scalars['String']>;
  clubName?: Maybe<Scalars['String']>;
  clubNamePartial?: Maybe<Scalars['String']>;
  clubType?: Maybe<ClubTypeFilterValue>;
  clubTypes?: Maybe<Array<ClubTypeFilterValue>>;
  earliestMeetingTime?: Maybe<Scalars['String']>;
  isSearchByClubName?: Maybe<Scalars['Boolean']>;
  keyword?: Maybe<Scalars['String']>;
  latestMeetingTime?: Maybe<Scalars['String']>;
  meetingDay?: Maybe<Scalars['String']>;
  meetingLanguageId?: Maybe<Scalars['String']>;
  meetingLocationLatitude?: Maybe<Scalars['Float']>;
  meetingLocationLongitude?: Maybe<Scalars['Float']>;
  meetingLocationRange?: Maybe<Scalars['Float']>;
  meetingTime?: Maybe<Scalars['String']>;
  meetingType?: Maybe<MeetingTypeFilterValue>;
  orderBy?: Maybe<Scalars['String']>;
  organizationBase?: Maybe<Scalars['String']>;
  page?: Maybe<Scalars['Int']>;
  pageSize?: Maybe<Scalars['Int']>;
  riClubId?: Maybe<Scalars['String']>;
  riDistrictId?: Maybe<Scalars['Int']>;
  status?: Maybe<Scalars['String']>;
};

export type DominoEmailInput = {
  action?: Maybe<Scalars['String']>;
  email?: Maybe<Scalars['String']>;
  id?: Maybe<Scalars['String']>;
  isPrimary?: Maybe<Scalars['Boolean']>;
};

export type BaseAddressInput = {
  action?: Maybe<Scalars['String']>;
  city: Scalars['String'];
  country?: Maybe<Scalars['String']>;
  countryId: Scalars['String'];
  id?: Maybe<Scalars['String']>;
  internationalProvince?: Maybe<Scalars['String']>;
  lineOne: Scalars['String'];
  lineThree?: Maybe<Scalars['String']>;
  lineTwo?: Maybe<Scalars['String']>;
  postalCode?: Maybe<Scalars['String']>;
  state?: Maybe<Scalars['String']>;
  stateId?: Maybe<Scalars['String']>;
};

export enum MeetingType {
  Hybrid = 'Hybrid',
  Online = 'Online',
  Physical = 'Physical',
}

export enum WeekdayFull {
  Friday = 'Friday',
  Monday = 'Monday',
  Saturday = 'Saturday',
  Sunday = 'Sunday',
  Thursday = 'Thursday',
  Tuesday = 'Tuesday',
  Wednesday = 'Wednesday',
}

export type MeetingInput = {
  address?: Maybe<BaseAddressInput>;
  comment?: Maybe<Scalars['String']>;
  detailForJoiningRemotely?: Maybe<Scalars['String']>;
  frequency?: Maybe<Scalars['String']>;
  language?: Maybe<Scalars['String']>;
  languageId?: Maybe<Scalars['String']>;
  latitude?: Maybe<Scalars['Float']>;
  locationNameOrDetail?: Maybe<Scalars['String']>;
  locationVaries?: Maybe<Scalars['Boolean']>;
  longitude?: Maybe<Scalars['Float']>;
  meetingId?: Maybe<Scalars['String']>;
  name?: Maybe<Scalars['String']>;
  onlineLocation?: Maybe<Scalars['String']>;
  time?: Maybe<Scalars['String']>;
  type: MeetingType;
  weekday?: Maybe<WeekdayFull>;
};

export type BasicLanguageInput = {
  action?: Maybe<Scalars['String']>;
  id?: Maybe<Scalars['String']>;
  languageId: Scalars['String'];
};

export type FaxInput = {
  action?: Maybe<Scalars['String']>;
  country?: Maybe<Scalars['String']>;
  countryId: Scalars['String'];
  extension?: Maybe<Scalars['String']>;
  id?: Maybe<Scalars['String']>;
  number: Scalars['String'];
  type?: Maybe<FaxType>;
};

export enum FaxType {
  Business = 'Business',
  Home = 'Home',
}

export type PhoneInput = {
  action?: Maybe<Scalars['String']>;
  country?: Maybe<Scalars['String']>;
  countryId: Scalars['String'];
  extension?: Maybe<Scalars['String']>;
  id?: Maybe<Scalars['String']>;
  number: Scalars['String'];
};

export type WebsiteInput = {
  action?: Maybe<Scalars['String']>;
  id?: Maybe<Scalars['String']>;
  otherWebsites?: Maybe<Scalars['String']>;
  primaryWebsite?: Maybe<Scalars['String']>;
};

export type UpdateClubInput = {
  charteredDate?: Maybe<Scalars['String']>;
  clubId: Scalars['String'];
  clubType?: Maybe<Scalars['String']>;
  district?: Maybe<Scalars['String']>;
  dominoId?: Maybe<Scalars['String']>;
  email?: Maybe<DominoEmailInput>;
  mailingAddress?: Maybe<BaseAddressInput>;
  meetings?: Maybe<Array<MeetingInput>>;
  name?: Maybe<Scalars['String']>;
  officialLanguage?: Maybe<BasicLanguageInput>;
  physicalLocation?: Maybe<BaseAddressInput>;
  primaryEmail?: Maybe<Scalars['String']>;
  primaryFax?: Maybe<FaxInput>;
  primaryPhone?: Maybe<PhoneInput>;
  primaryWebsite?: Maybe<Scalars['String']>;
  riClubId?: Maybe<Scalars['String']>;
  type?: Maybe<Scalars['String']>;
  website?: Maybe<WebsiteInput>;
};

export type ClubSearchResutLocation = {
  __typename?: 'ClubSearchResutLocation';
  city?: Maybe<Scalars['String']>;
  country?: Maybe<Scalars['String']>;
  countryId?: Maybe<Scalars['String']>;
  internationalProvince?: Maybe<Scalars['String']>;
  lineOne?: Maybe<Scalars['String']>;
  lineThree?: Maybe<Scalars['String']>;
  lineTwo?: Maybe<Scalars['String']>;
  postalCode?: Maybe<Scalars['String']>;
  state?: Maybe<Scalars['String']>;
  stateId?: Maybe<Scalars['String']>;
};

export type InputConferenceAddress = {
  city?: Maybe<Scalars['String']>;
  country?: Maybe<Scalars['String']>;
  internationalProvince?: Maybe<Scalars['String']>;
  lineOne?: Maybe<Scalars['String']>;
  lineThree?: Maybe<Scalars['String']>;
  lineTwo?: Maybe<Scalars['String']>;
  postalCode?: Maybe<Scalars['String']>;
  state?: Maybe<Scalars['String']>;
};

export type ParticipatingDistrictsInput = {
  id: Scalars['String'];
  riDistrictId: Scalars['Int'];
};

export type InputAccomodationPhone = {
  countryId: Scalars['String'];
  extention?: Maybe<Scalars['String']>;
  number: Scalars['String'];
  type: Scalars['String'];
};

export type InputCreateConference = {
  comments?: Maybe<Scalars['String']>;
  conferenceAddress?: Maybe<InputConferenceAddress>;
  detailForJoiningRemotely?: Maybe<Scalars['String']>;
  districtId: Scalars['String'];
  endDate?: Maybe<Scalars['String']>;
  endTime?: Maybe<Scalars['String']>;
  isPresRepRequested?: Maybe<Scalars['Boolean']>;
  language?: Maybe<Scalars['String']>;
  onlineLocation?: Maybe<Scalars['String']>;
  participatingDistricts?: Maybe<Array<ParticipatingDistrictsInput>>;
  prAccommodation?: Maybe<Scalars['String']>;
  prAddress?: Maybe<InputConferenceAddress>;
  presRepAccPhone?: Maybe<InputAccomodationPhone>;
  startDate?: Maybe<Scalars['String']>;
  startTime?: Maybe<Scalars['String']>;
  timeZone?: Maybe<Scalars['String']>;
  venueName?: Maybe<Scalars['String']>;
  venueType: Scalars['String'];
};

export type ConferenceAddress = {
  __typename?: 'ConferenceAddress';
  city?: Maybe<Scalars['String']>;
  country?: Maybe<Scalars['String']>;
  internationalProvince?: Maybe<Scalars['String']>;
  lineOne?: Maybe<Scalars['String']>;
  lineThree?: Maybe<Scalars['String']>;
  lineTwo?: Maybe<Scalars['String']>;
  postalCode?: Maybe<Scalars['String']>;
  state?: Maybe<Scalars['String']>;
};

export type ConferenceFeedbackResult = {
  __typename?: 'ConferenceFeedbackResult';
  id: Scalars['String'];
  type: Scalars['String'];
};

export type ParticipatingDistricts = {
  __typename?: 'ParticipatingDistricts';
  id: Scalars['String'];
  riDistrictId: Scalars['Int'];
};

export type PresidentialRepresentative = {
  __typename?: 'PresidentialRepresentative';
  firstName: Scalars['String'];
  id: Scalars['String'];
  lastName: Scalars['String'];
  middleName?: Maybe<Scalars['String']>;
};

export type GetConferencesResults = {
  __typename?: 'GetConferencesResults';
  conferenceAddress?: Maybe<ConferenceAddress>;
  createdByRiDistrictId?: Maybe<Scalars['String']>;
  districtId: Scalars['String'];
  endDate: Scalars['String'];
  feedbacks: Array<ConferenceFeedbackResult>;
  id: Scalars['String'];
  isDistrictPresRep?: Maybe<Scalars['Boolean']>;
  participatingDistricts?: Maybe<Array<ParticipatingDistricts>>;
  presidentialRepresentative?: Maybe<PresidentialRepresentative>;
  relatedRotaryYear: Scalars['Int'];
  startDate: Scalars['String'];
  venueType: MeetingType;
};

export type FeedbackSection = {
  __typename?: 'FeedbackSection';
  id: Scalars['String'];
  sectionName: Scalars['String'];
};

export type OptionDetail = {
  __typename?: 'OptionDetail';
  id: Scalars['String'];
  optionValue?: Maybe<Scalars['String']>;
};

export type OptionType = {
  __typename?: 'OptionType';
  id: Scalars['String'];
  optionType: Scalars['String'];
};

export type Question = {
  __typename?: 'Question';
  applicationFeature: Scalars['String'];
  applicationId: Scalars['String'];
  feedbackSection: FeedbackSection;
  id: Scalars['String'];
  option: Scalars['String'];
  optionDataType: Scalars['String'];
  optionDetail: OptionDetail;
  optionSeq: Scalars['Int'];
  optionType: OptionType;
};

export type QuestionResponse = {
  __typename?: 'QuestionResponse';
  questionId: Scalars['String'];
  value?: Maybe<Scalars['String']>;
};

export type Feedback = {
  __typename?: 'Feedback';
  feedbackType: Scalars['String'];
  id: Scalars['String'];
  isDistrictPresRep?: Maybe<Scalars['Boolean']>;
  questions: Array<Question>;
  responses: Array<QuestionResponse>;
};

export type InputFeedbackSection = {
  id: Scalars['String'];
  sectionName: Scalars['String'];
};

export type InputOptionDetail = {
  id: Scalars['String'];
  optionValue: Scalars['String'];
};

export type InputOptionType = {
  id: Scalars['String'];
  optionType: Scalars['String'];
};

export type InputQuestion = {
  applicationFeature: Scalars['String'];
  applicationId: Scalars['String'];
  feedbackSection: InputFeedbackSection;
  id: Scalars['String'];
  option: Scalars['String'];
  optionDataType: Scalars['String'];
  optionDetail: InputOptionDetail;
  optionSeq: Scalars['Int'];
  optionType: InputOptionType;
};

export type InputQuestionResponse = {
  questionId: Scalars['String'];
  value: Scalars['String'];
};

export type InputCreateFeedback = {
  conferenceId: Scalars['String'];
  feedbackType: Scalars['String'];
  questions: Array<InputQuestion>;
  questionsResponse: Array<InputQuestionResponse>;
};

export type AccPhone = {
  __typename?: 'AccPhone';
  countryId?: Maybe<Scalars['String']>;
  number: Scalars['String'];
};

export type GetConferenceDetailsResult = {
  __typename?: 'GetConferenceDetailsResult';
  comments?: Maybe<Scalars['String']>;
  conferenceAddress?: Maybe<ConferenceAddress>;
  createdByRiDistrictId?: Maybe<Scalars['String']>;
  detailForJoiningRemotely?: Maybe<Scalars['String']>;
  districtId: Scalars['String'];
  endDate?: Maybe<Scalars['String']>;
  endTime?: Maybe<Scalars['String']>;
  id: Scalars['String'];
  isPresRepRequested?: Maybe<Scalars['Boolean']>;
  language?: Maybe<Scalars['String']>;
  locationNameOrDetail?: Maybe<Scalars['String']>;
  onlineLocation?: Maybe<Scalars['String']>;
  participatingDistricts?: Maybe<Array<ParticipatingDistricts>>;
  prAccPhone?: Maybe<Scalars['String']>;
  prAccommodation?: Maybe<Scalars['String']>;
  prAddress?: Maybe<ConferenceAddress>;
  presRepAccPhone?: Maybe<AccPhone>;
  presidentialRepresentative?: Maybe<PresidentialRepresentative>;
  relatedRotaryYear: Scalars['Int'];
  startDate?: Maybe<Scalars['String']>;
  startTime?: Maybe<Scalars['String']>;
  stateProvince?: Maybe<Scalars['String']>;
  timeZone?: Maybe<Scalars['String']>;
  venueName?: Maybe<Scalars['String']>;
  venueType: MeetingType;
};

export type FetchConferenceQuery = {
  __typename?: 'Query';
  conference?:
    | {
        __typename?: 'GetConferenceDetailsResult';
        id: string;
        districtId: string;
        relatedRotaryYear: number;
        venueType: MeetingType;
        venueName?: string | null | undefined;
        startDate?: string | null | undefined;
        startTime?: string | null | undefined;
        timeZone?: string | null | undefined;
        endDate?: string | null | undefined;
        endTime?: string | null | undefined;
        language?: string | null | undefined;
        prAccommodation?: string | null | undefined;
        onlineLocation?: string | null | undefined;
        locationNameOrDetail?: string | null | undefined;
        detailForJoiningRemotely?: string | null | undefined;
        comments?: string | null | undefined;
        createdByRiDistrictId?: string | null | undefined;
        isPresRepRequested?: boolean | null | undefined;
        prAddress?:
          | {
              __typename?: 'ConferenceAddress';
              lineOne?: string | null | undefined;
              lineTwo?: string | null | undefined;
              lineThree?: string | null | undefined;
              country?: string | null | undefined;
              city?: string | null | undefined;
              state?: string | null | undefined;
              postalCode?: string | null | undefined;
              internationalProvince?: string | null | undefined;
            }
          | null
          | undefined;
        presidentialRepresentative?:
          | {
              __typename?: 'PresidentialRepresentative';
              id: string;
              firstName: string;
              middleName?: string | null | undefined;
              lastName: string;
            }
          | null
          | undefined;
        participatingDistricts?:
          | Array<{
              __typename?: 'ParticipatingDistricts';
              id: string;
              riDistrictId: number;
            }>
          | null
          | undefined;
        conferenceAddress?:
          | {
              __typename?: 'ConferenceAddress';
              lineOne?: string | null | undefined;
              lineTwo?: string | null | undefined;
              lineThree?: string | null | undefined;
              country?: string | null | undefined;
              city?: string | null | undefined;
              state?: string | null | undefined;
              postalCode?: string | null | undefined;
              internationalProvince?: string | null | undefined;
            }
          | null
          | undefined;
        presRepAccPhone?:
          | {
              __typename?: 'AccPhone';
              number: string;
              countryId?: string | null | undefined;
            }
          | null
          | undefined;
      }
    | null
    | undefined;
};

export type FeedbackQuestionsResponse = {
  __typename?: 'FeedbackQuestionsResponse';
  id: Scalars['String'];
  questions: Array<Question>;
  sectionName: Scalars['String'];
};

export enum OrganizationBaseEnum {
  Community = 'Community',
  School = 'School',
  University = 'University',
}
